// UI Manager for handling different screens and user interactions
// Requirements: 1.1, 1.2, 2.1, 2.3, 7.4, 7.5

import { generateLobbyUrl } from '../shared/utils';

export interface UIManagerCallbacks {
  onCreateGame: () => Promise<string>; // Returns lobby ID
  onJoinLobby: (pseudonym: string) => Promise<void>;
  onStartGame: () => Promise<void>;
  onPlayAgain: () => Promise<void>;
  onLeaveLobby: () => Promise<void>;
  onMobileInput?: (action: 'jump' | 'move-left' | 'move-right', pressed: boolean) => void;
}

export interface PlayerInfo {
  id: string;
  pseudonym: string;
  isHost?: boolean;
  isAlive?: boolean;
  score?: number;
}

export class UIManager {
  private callbacks: UIManagerCallbacks;
  private currentScreen: string = 'homepage';
  private lobbyId: string | null = null;
  private isHost: boolean = false;
  private players: PlayerInfo[] = [];
  private scoreboard: Record<string, number> = {};
  private mobileControlsActive: boolean = false;
  private activeButtons: Set<string> = new Set();

  constructor(callbacks: UIManagerCallbacks) {
    this.callbacks = callbacks;
    this.initializeEventListeners();
  }

  /**
   * Initialize event listeners for UI interactions
   */
  private initializeEventListeners(): void {
    // Homepage
    const createGameBtn = document.getElementById('create-game-btn');
    createGameBtn?.addEventListener('click', this.handleCreateGame.bind(this));

    // Lobby host screen
    const copyUrlBtn = document.getElementById('copy-url-btn');
    copyUrlBtn?.addEventListener('click', this.handleCopyUrl.bind(this));

    const shareUrlBtn = document.getElementById('share-url-btn');
    shareUrlBtn?.addEventListener('click', this.handleShareUrl.bind(this));

    const startGameBtn = document.getElementById('start-game-btn');
    startGameBtn?.addEventListener('click', this.handleStartGame.bind(this));

    // Join lobby screen
    const joinLobbyBtn = document.getElementById('join-lobby-btn');
    joinLobbyBtn?.addEventListener('click', this.handleJoinLobby.bind(this));

    const pseudonymInput = document.getElementById('pseudonym-input') as HTMLInputElement;
    pseudonymInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.handleJoinLobby();
      }
    });

    // Results screen
    const playAgainBtn = document.getElementById('play-again-btn');
    playAgainBtn?.addEventListener('click', this.handlePlayAgain.bind(this));

    const leaveLobbyBtn = document.getElementById('leave-lobby-btn');
    leaveLobbyBtn?.addEventListener('click', this.handleLeaveLobby.bind(this));

    // Error screen
    const backToHomeBtn = document.getElementById('back-to-home-btn');
    backToHomeBtn?.addEventListener('click', this.handleBackToHome.bind(this));

    // Check for Web Share API support
    if ('share' in navigator) {
      const shareBtn = document.getElementById('share-url-btn');
      if (shareBtn) {
        shareBtn.style.display = 'inline-block';
      }
    }

    // Initialize mobile controls
    this.initializeMobileControls();
  }

  /**
   * Initialize mobile control event listeners
   * Requirements: 4.3 - Mobile controls with dedicated buttons
   */
  private initializeMobileControls(): void {
    const mobileControls = document.getElementById('mobile-controls');
    if (!mobileControls) return;

    // Get all mobile control buttons
    const controlButtons = mobileControls.querySelectorAll('.mobile-control-btn');
    
    controlButtons.forEach(button => {
      const action = button.getAttribute('data-action') as 'jump' | 'move-left' | 'move-right';
      if (!action) return;

      // Touch events for mobile
      button.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.handleMobileButtonPress(action, true, button as HTMLElement);
      }, { passive: false });

      button.addEventListener('touchend', (e) => {
        e.preventDefault();
        this.handleMobileButtonPress(action, false, button as HTMLElement);
      }, { passive: false });

      button.addEventListener('touchcancel', (e) => {
        e.preventDefault();
        this.handleMobileButtonPress(action, false, button as HTMLElement);
      }, { passive: false });

      // Mouse events for desktop testing
      button.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.handleMobileButtonPress(action, true, button as HTMLElement);
      });

      button.addEventListener('mouseup', (e) => {
        e.preventDefault();
        this.handleMobileButtonPress(action, false, button as HTMLElement);
      });

      button.addEventListener('mouseleave', (e) => {
        e.preventDefault();
        this.handleMobileButtonPress(action, false, button as HTMLElement);
      });

      // Prevent context menu on long press
      button.addEventListener('contextmenu', (e) => {
        e.preventDefault();
      });
    });

    // Detect if device is mobile to show controls automatically
    this.detectMobileDevice();
  }

  /**
   * Handle mobile button press with visual feedback and haptic feedback
   */
  private handleMobileButtonPress(action: 'jump' | 'move-left' | 'move-right', pressed: boolean, buttonElement: HTMLElement): void {
    const buttonId = `${action}-btn`;
    
    if (pressed) {
      // Add visual feedback
      buttonElement.classList.add('pressed');
      this.activeButtons.add(buttonId);
      
      // Haptic feedback (if supported)
      this.triggerHapticFeedback();
    } else {
      // Remove visual feedback
      buttonElement.classList.remove('pressed');
      this.activeButtons.delete(buttonId);
    }

    // Call the callback if provided
    if (this.callbacks.onMobileInput) {
      this.callbacks.onMobileInput(action, pressed);
    }
  }

  /**
   * Trigger haptic feedback if supported
   */
  private triggerHapticFeedback(): void {
    // Check for haptic feedback support
    if ('vibrate' in navigator) {
      // Short vibration for button press
      navigator.vibrate(50);
    }
    
    // Check for more advanced haptic feedback (iOS Safari)
    if ('hapticFeedback' in window) {
      try {
        (window as any).hapticFeedback.impact('light');
      } catch (error) {
        // Haptic feedback not available
      }
    }
  }

  /**
   * Detect if device is mobile and show controls accordingly
   */
  private detectMobileDevice(): void {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     ('ontouchstart' in window) ||
                     (window.innerWidth <= 768);
    
    if (isMobile) {
      this.showMobileControls();
    }
  }

  /**
   * Show mobile controls
   */
  showMobileControls(): void {
    const mobileControls = document.getElementById('mobile-controls');
    const gameScreen = document.getElementById('game-screen');
    
    if (mobileControls && gameScreen) {
      mobileControls.classList.add('active');
      gameScreen.classList.add('game-with-mobile-controls');
      this.mobileControlsActive = true;
    }
  }

  /**
   * Hide mobile controls
   */
  hideMobileControls(): void {
    const mobileControls = document.getElementById('mobile-controls');
    const gameScreen = document.getElementById('game-screen');
    
    if (mobileControls && gameScreen) {
      mobileControls.classList.remove('active');
      gameScreen.classList.remove('game-with-mobile-controls');
      this.mobileControlsActive = false;
      
      // Clear any active button states
      this.clearActiveButtons();
    }
  }

  /**
   * Clear all active button states
   */
  private clearActiveButtons(): void {
    this.activeButtons.forEach(buttonId => {
      const button = document.getElementById(buttonId);
      if (button) {
        button.classList.remove('pressed');
      }
    });
    this.activeButtons.clear();
  }

  /**
   * Show a specific screen and hide others
   */
  showScreen(screenId: string): void {
    // Hide all screens
    const screens = document.querySelectorAll('.screen');
    screens.forEach(screen => screen.classList.remove('active'));

    // Hide mobile controls when leaving game screen
    if (this.currentScreen === 'game' && screenId !== 'game') {
      this.hideMobileControls();
    }

    // Show the requested screen
    const targetScreen = document.getElementById(`${screenId}-screen`);
    if (targetScreen) {
      targetScreen.classList.add('active');
      this.currentScreen = screenId;
    }
  }

  /**
   * Show the homepage screen
   * Requirements: 1.1
   */
  showHomepage(): void {
    this.showScreen('homepage');
    this.resetState();
  }

  /**
   * Show the lobby creation screen
   */
  showLobbyCreation(): void {
    this.showScreen('lobby-creation');
  }

  /**
   * Show the lobby host screen with URL sharing
   * Requirements: 1.2
   */
  showLobbyHost(lobbyId: string): void {
    this.lobbyId = lobbyId;
    this.isHost = true;
    
    // Generate and display lobby URL
    const baseUrl = window.location.origin;
    const lobbyUrl = generateLobbyUrl(baseUrl, lobbyId);
    
    const urlElement = document.getElementById('lobby-url-text');
    if (urlElement) {
      urlElement.textContent = lobbyUrl;
    }

    this.showScreen('lobby-host');
    this.updatePlayerList();
  }

  /**
   * Show the join lobby screen
   * Requirements: 2.1
   */
  showJoinLobby(): void {
    this.showScreen('join-lobby');
    
    // Focus on pseudonym input
    const pseudonymInput = document.getElementById('pseudonym-input') as HTMLInputElement;
    if (pseudonymInput) {
      pseudonymInput.focus();
    }
  }

  /**
   * Show the lobby waiting screen for non-host players
   * Requirements: 2.3
   */
  showLobbyWaiting(): void {
    this.showScreen('lobby-waiting');
    this.updateWaitingPlayerList();
  }

  /**
   * Show the game screen
   */
  showGame(): void {
    this.showScreen('game');
    
    // Show mobile controls if on mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     ('ontouchstart' in window) ||
                     (window.innerWidth <= 768);
    
    if (isMobile) {
      this.showMobileControls();
    }
  }

  /**
   * Show the game results screen
   * Requirements: 7.4, 7.5
   */
  showResults(winnerId: string | null, finalStandings: PlayerInfo[], scoreboard: Record<string, number>): void {
    this.scoreboard = scoreboard;
    
    // Update winner announcement
    const winnerElement = document.getElementById('winner-announcement');
    if (winnerElement) {
      if (winnerId) {
        const winner = finalStandings.find(p => p.id === winnerId);
        winnerElement.innerHTML = `<p>🏆 Winner: ${winner?.pseudonym || 'Unknown'} 🏆</p>`;
      } else {
        winnerElement.innerHTML = '<p>Game ended with no winner</p>';
      }
    }

    // Update final standings
    this.updateResultsList(finalStandings);
    
    // Update scoreboard
    this.updateScoreboard(scoreboard);
    
    this.showScreen('results');
  }

  /**
   * Show error screen with message
   */
  showError(message: string): void {
    const errorElement = document.getElementById('error-message');
    if (errorElement) {
      errorElement.innerHTML = `<p>${message}</p>`;
    }
    this.showScreen('error');
  }

  /**
   * Update the player list in the lobby
   */
  updatePlayerList(): void {
    const container = document.getElementById('player-list-container');
    if (!container) return;

    container.innerHTML = '';
    
    this.players.forEach(player => {
      const playerElement = document.createElement('div');
      playerElement.className = 'player-item';
      
      const nameElement = document.createElement('div');
      nameElement.className = 'player-name';
      nameElement.textContent = player.pseudonym;
      
      const statusElement = document.createElement('div');
      statusElement.className = 'player-status';
      statusElement.textContent = player.isHost ? 'Host' : 'Player';
      
      playerElement.appendChild(nameElement);
      playerElement.appendChild(statusElement);
      container.appendChild(playerElement);
    });

    // Update start game button state
    if (this.isHost) {
      const startBtn = document.getElementById('start-game-btn') as HTMLButtonElement;
      if (startBtn) {
        const canStart = this.players.length >= 2;
        startBtn.disabled = !canStart;
        startBtn.textContent = canStart ? 'Start Game' : `Start Game (Need ${2 - this.players.length} more players)`;
      }
    }
  }

  /**
   * Update the player list in the waiting screen
   */
  updateWaitingPlayerList(): void {
    const container = document.getElementById('waiting-player-list-container');
    if (!container) return;

    container.innerHTML = '';
    
    this.players.forEach(player => {
      const playerElement = document.createElement('div');
      playerElement.className = 'player-item';
      
      const nameElement = document.createElement('div');
      nameElement.className = 'player-name';
      nameElement.textContent = player.pseudonym;
      
      const statusElement = document.createElement('div');
      statusElement.className = 'player-status';
      statusElement.textContent = player.isHost ? 'Host' : 'Ready';
      
      playerElement.appendChild(nameElement);
      playerElement.appendChild(statusElement);
      container.appendChild(playerElement);
    });
  }

  /**
   * Update the results list
   */
  updateResultsList(finalStandings: PlayerInfo[]): void {
    const container = document.getElementById('results-list-container');
    if (!container) return;

    container.innerHTML = '';
    
    finalStandings.forEach((player, index) => {
      const playerElement = document.createElement('div');
      playerElement.className = 'player-item';
      
      const nameElement = document.createElement('div');
      nameElement.className = 'player-name';
      nameElement.textContent = `${index + 1}. ${player.pseudonym}`;
      
      const statusElement = document.createElement('div');
      statusElement.className = 'player-status';
      statusElement.textContent = player.isAlive ? 'Winner' : 'Eliminated';
      
      playerElement.appendChild(nameElement);
      playerElement.appendChild(statusElement);
      container.appendChild(playerElement);
    });
  }

  /**
   * Update the scoreboard display
   */
  updateScoreboard(scoreboard: Record<string, number>): void {
    const container = document.getElementById('scoreboard-container');
    if (!container) return;

    container.innerHTML = '';
    
    // Sort players by score (descending)
    const sortedPlayers = Object.entries(scoreboard)
      .sort(([, a], [, b]) => b - a);
    
    sortedPlayers.forEach(([playerId, score]) => {
      const player = this.players.find(p => p.id === playerId);
      if (!player) return;
      
      const playerElement = document.createElement('div');
      playerElement.className = 'player-item';
      
      const nameElement = document.createElement('div');
      nameElement.className = 'player-name';
      nameElement.textContent = player.pseudonym;
      
      const scoreElement = document.createElement('div');
      scoreElement.className = 'player-status';
      scoreElement.textContent = `${score} wins`;
      
      playerElement.appendChild(nameElement);
      playerElement.appendChild(scoreElement);
      container.appendChild(playerElement);
    });
  }

  /**
   * Add or update a player in the list
   */
  addOrUpdatePlayer(player: PlayerInfo): void {
    const existingIndex = this.players.findIndex(p => p.id === player.id);
    if (existingIndex >= 0) {
      this.players[existingIndex] = { ...this.players[existingIndex], ...player };
    } else {
      this.players.push(player);
    }
    
    // Update the appropriate player list based on current screen
    if (this.currentScreen === 'lobby-host') {
      this.updatePlayerList();
    } else if (this.currentScreen === 'lobby-waiting') {
      this.updateWaitingPlayerList();
    }
  }

  /**
   * Remove a player from the list
   */
  removePlayer(playerId: string): void {
    this.players = this.players.filter(p => p.id !== playerId);
    
    // Update the appropriate player list based on current screen
    if (this.currentScreen === 'lobby-host') {
      this.updatePlayerList();
    } else if (this.currentScreen === 'lobby-waiting') {
      this.updateWaitingPlayerList();
    }
  }

  /**
   * Show status message
   */
  showStatus(message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info'): void {
    let statusElement: HTMLElement | null = null;
    
    if (this.currentScreen === 'join-lobby') {
      statusElement = document.getElementById('join-status');
    } else if (this.currentScreen === 'lobby-waiting') {
      statusElement = document.getElementById('lobby-status');
    } else if (this.currentScreen === 'game') {
      statusElement = document.getElementById('game-status');
    }
    
    if (statusElement) {
      statusElement.className = `status-message ${type}`;
      statusElement.innerHTML = `<p>${message}</p>`;
      statusElement.style.display = 'block';
    }
  }

  /**
   * Handle create game button click
   */
  private async handleCreateGame(): Promise<void> {
    try {
      this.showLobbyCreation();
      const lobbyId = await this.callbacks.onCreateGame();
      this.showLobbyHost(lobbyId);
    } catch (error) {
      console.error('Failed to create game:', error);
      // Show more detailed error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      this.showError(`Failed to create game: ${errorMessage}`);
    }
  }

  /**
   * Handle copy URL button click
   */
  private async handleCopyUrl(): Promise<void> {
    const urlElement = document.getElementById('lobby-url-text');
    if (!urlElement) return;

    try {
      await navigator.clipboard.writeText(urlElement.textContent || '');
      
      // Show temporary feedback
      const copyBtn = document.getElementById('copy-url-btn');
      if (copyBtn) {
        const originalText = copyBtn.textContent;
        copyBtn.textContent = 'Copied!';
        setTimeout(() => {
          copyBtn.textContent = originalText;
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to copy URL:', error);
      // Fallback: select the text
      const range = document.createRange();
      range.selectNode(urlElement);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
    }
  }

  /**
   * Handle share URL button click (Web Share API)
   */
  private async handleShareUrl(): Promise<void> {
    const urlElement = document.getElementById('lobby-url-text');
    if (!urlElement || !navigator.share) return;

    try {
      await navigator.share({
        title: 'Join my Multiplayer Endless Runner game!',
        text: 'Come play this fun jumping game with me!',
        url: urlElement.textContent || ''
      });
    } catch (error) {
      console.error('Failed to share URL:', error);
    }
  }

  /**
   * Handle join lobby button click
   */
  private async handleJoinLobby(): Promise<void> {
    const pseudonymInput = document.getElementById('pseudonym-input') as HTMLInputElement;
    if (!pseudonymInput) return;

    const pseudonym = pseudonymInput.value.trim();
    if (!pseudonym) {
      this.showStatus('Please enter your name', 'error');
      return;
    }

    if (pseudonym.length > 20) {
      this.showStatus('Name must be 20 characters or less', 'error');
      return;
    }

    try {
      this.showStatus('Joining lobby...', 'info');
      await this.callbacks.onJoinLobby(pseudonym);
      this.showLobbyWaiting();
    } catch (error) {
      console.error('Failed to join lobby:', error);
      this.showStatus('Failed to join lobby. Please try again.', 'error');
    }
  }

  /**
   * Handle start game button click
   */
  private async handleStartGame(): Promise<void> {
    if (!this.isHost || this.players.length < 2) return;

    try {
      await this.callbacks.onStartGame();
    } catch (error) {
      console.error('Failed to start game:', error);
      this.showError('Failed to start game. Please try again.');
    }
  }

  /**
   * Handle play again button click
   */
  private async handlePlayAgain(): Promise<void> {
    try {
      await this.callbacks.onPlayAgain();
      if (this.isHost) {
        this.showLobbyHost(this.lobbyId!);
      } else {
        this.showLobbyWaiting();
      }
    } catch (error) {
      console.error('Failed to start new game:', error);
      this.showError('Failed to start new game. Please try again.');
    }
  }

  /**
   * Handle leave lobby button click
   */
  private async handleLeaveLobby(): Promise<void> {
    try {
      await this.callbacks.onLeaveLobby();
      this.showHomepage();
    } catch (error) {
      console.error('Failed to leave lobby:', error);
      this.showHomepage(); // Go home anyway
    }
  }

  /**
   * Handle back to home button click
   */
  private handleBackToHome(): void {
    this.showHomepage();
  }

  /**
   * Reset internal state
   */
  private resetState(): void {
    this.lobbyId = null;
    this.isHost = false;
    this.players = [];
    this.scoreboard = {};
  }

  /**
   * Get current screen
   */
  getCurrentScreen(): string {
    return this.currentScreen;
  }

  /**
   * Check if user is host
   */
  getIsHost(): boolean {
    return this.isHost;
  }

  /**
   * Check if mobile controls are active
   */
  areMobileControlsActive(): boolean {
    return this.mobileControlsActive;
  }

  /**
   * Get currently active buttons (for testing)
   */
  getActiveButtons(): string[] {
    return Array.from(this.activeButtons);
  }
}